// Shared presentational primitives — consistent, digestible look across the app.
import { useEffect, useRef, useState, type ReactNode } from "react";

export function InfoTip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <span ref={ref} className="relative inline-flex align-middle group/tip">
      <button type="button" aria-label="info" onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="ml-1 w-4 h-4 inline-flex items-center justify-center rounded-full border border-edge text-[10px] leading-none text-muted hover:text-ink hover:border-ink/40 active:scale-95">i</button>
      <span className={`${open ? "block" : "hidden"} group-hover/tip:block absolute z-50 left-0 top-5 w-64 max-w-[78vw] p-2.5 rounded-lg bg-ink text-white text-[11px] font-normal normal-case leading-relaxed shadow-xl whitespace-pre-line text-left`}>{text}</span>
    </span>
  );
}

export function StatCard({ label, value, sub, accent, tone }: { label: string; value: string; sub?: string; accent?: boolean; tone?: "ok" | "warn" | "danger" }) {
  const toneCls = tone === "danger" ? "text-danger" : tone === "warn" ? "text-warn" : tone === "ok" ? "text-ok" : "";
  return (
    <div className={`rounded-xl p-3.5 border ${accent ? "bg-brand/10 border-brand/20" : "bg-paper border-edge/70"}`}>
      <div className={`text-xs ${accent ? "text-brand" : "text-muted"}`}>{label}</div>
      <div className={`text-2xl font-semibold mt-1 tabular-nums tracking-tight ${accent ? "text-brand" : toneCls || "text-ink"}`}>{value}</div>
      {sub && <div className={`text-[11px] mt-0.5 ${accent ? "text-brand/80" : "text-muted"}`}>{sub}</div>}
    </div>
  );
}

export function StatGrid({ children, cols = 4 }: { children: ReactNode; cols?: 2 | 3 | 4 }) {
  const c = cols === 2 ? "sm:grid-cols-2" : cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-4";
  return <div className={`grid grid-cols-2 ${c} gap-2.5`}>{children}</div>;
}

export function SegTabs<T extends string>({ tabs, value, onChange, className = "" }: {
  tabs: { id: T; label: string; icon?: ReactNode }[]; value: T; onChange: (v: T) => void; className?: string;
}) {
  return (
    <div className={`flex gap-1 p-1 bg-paper rounded-xl border border-edge/70 w-fit max-w-full overflow-x-auto no-scrollbar ${className}`}>
      {tabs.map((t) => (
        <button key={t.id} onClick={() => onChange(t.id)}
          className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition ${value === t.id ? "bg-panel text-ink shadow-card" : "text-muted hover:text-ink"}`}>
          {t.icon}{t.label}
        </button>
      ))}
    </div>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`skeleton ${className}`} aria-hidden="true" />;
}
export function SkeletonText({ lines = 3, className = "" }: { lines?: number; className?: string }) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className={`h-3 ${i === lines - 1 ? "w-2/3" : "w-full"}`} />
      ))}
    </div>
  );
}
export function SkeletonCard({ lines = 4 }: { lines?: number }) {
  return (
    <div className="card" aria-busy="true">
      <Skeleton className="h-4 w-1/3 mb-3" />
      <SkeletonText lines={lines} />
    </div>
  );
}

const TONES = {
  ok: "bg-ok/10 text-ok border-ok/20",
  warn: "bg-warn/10 text-warn border-warn/20",
  danger: "bg-danger/10 text-danger border-danger/20",
  brand: "bg-brand/10 text-brand border-brand/20",
  muted: "bg-paper text-muted border-edge",
};
export function Badge({ children, tone = "muted" }: { children: ReactNode; tone?: keyof typeof TONES }) {
  return <span className={`inline-block text-xs px-2 py-0.5 rounded-full border ${TONES[tone]}`}>{children}</span>;
}

export function Toggle({ label, sub, checked, onChange, disabled }: {
  label?: string; sub?: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <button type="button" disabled={disabled} onClick={() => onChange(!checked)} className="flex items-center gap-3 text-left disabled:opacity-50">
      <span className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? "bg-brand" : "bg-edge"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? "left-[1.125rem]" : "left-0.5"}`} />
      </span>
      {(label || sub) && <span>{label && <span className="text-sm font-medium block">{label}</span>}{sub && <span className="block text-muted text-xs">{sub}</span>}</span>}
    </button>
  );
}

export function EmptyState({ title, sub, action }: { title: string; sub?: string; action?: ReactNode }) {
  return (
    <div className="text-center py-12 px-4">
      <div className="text-sm font-medium text-ink">{title}</div>
      {sub && <div className="text-xs text-muted mt-1 max-w-md mx-auto">{sub}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 backdrop-blur-sm p-4 sm:p-8 animate-fade-in" onMouseDown={onClose}>
      <div className={`bg-panel rounded-2xl shadow-pop w-full ${wide ? "max-w-3xl" : "max-w-lg"} my-4 animate-fade-in-up`} onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-edge">
          <h3 className="text-sm font-semibold">{title}</h3>
          <button onClick={onClose} className="w-7 h-7 inline-flex items-center justify-center rounded-lg text-muted hover:bg-paper hover:text-ink" aria-label="close">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export function LabeledInput({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-muted mt-1">{hint}</span>}
    </label>
  );
}

// Buffered inline inputs — hold local state while typing and commit only on blur or Enter, so an
// autosaving parent (mutate → network save) never fires per-keystroke or disables the field mid-type.
export function InlineText({ value, onCommit, className = "input py-1", placeholder, title }: {
  value: string; onCommit: (v: string) => void; className?: string; placeholder?: string; title?: string;
}) {
  const [buf, setBuf] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setBuf(value); }, [value, focused]);
  return (
    <input className={className} value={buf} placeholder={placeholder} title={title}
      onFocus={() => setFocused(true)}
      onChange={(e) => setBuf(e.target.value)}
      onBlur={() => { setFocused(false); if (buf !== value) onCommit(buf); }}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
  );
}

export function InlineNum({ value, onCommit, className = "input py-1 text-right", placeholder, title, step = "any", min }: {
  value: number | null | undefined; onCommit: (v: number) => void; className?: string; placeholder?: string; title?: string; step?: string; min?: number;
}) {
  const str = value === null || value === undefined || Number.isNaN(value) ? "" : String(value);
  const [buf, setBuf] = useState(str);
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setBuf(str); }, [str, focused]);
  return (
    <input type="number" step={step} min={min} className={className} value={buf} placeholder={placeholder} title={title}
      onFocus={() => setFocused(true)}
      onChange={(e) => setBuf(e.target.value)}
      onBlur={() => { setFocused(false); const n = Number(buf) || 0; if (n !== value) onCommit(n); }}
      onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
  );
}
